/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/glass/controllers/WidgetBaseActionHandler',
	'../../../app/nls/StringResources',
	'../../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'../../widgets/staticwidget/ShapeWidget'
], function(_, BaseClass, stringResources, ClassFactory, ShapeWidget) {

	var ActionHandler = BaseClass.extend({

		getDashboardView: function(context) {
			return context.glassContext.appController.getCurrentContentView();
		},

		getUiHelperClass: function() {
			return ClassFactory.loadModule('dashboard-core/js/lib/@waca/dashboard-common/dist/ui/WidgetAddUIHelper');
		},

		createWidgetAddUIHelper: function(context) {
			var view = this.getDashboardView(context);

			this.uiHelperClassOptions = {
				dashboardApi: view.getDashboardApi(),
				services: view.services
			};

			return this.getUiHelperClass().then(function(UIHelperClass) {
				const widgetAddUIHelper = new UIHelperClass(this.uiHelperClassOptions);
				widgetAddUIHelper.panelAttributes = {
					canvasController: view.boardLoader.canvasController,
					cdnUrl: view.getCDNUrl(),
					ajaxSvc: view.ajaxSvc,
					glassContext: context.glassContext
				};
				return widgetAddUIHelper;
			}.bind(this));
		},

		getOptions: function(context) {
			var view = this.getDashboardView(context);
			var options = {
				className: 'widgetsPanelSlideout',
				title: stringResources.get('widgetsPanelTitle'),
				module: 'dashboard-core/js/dashboard/views/WidgetsPanelView',
				getEntries: this.getEntries.bind(this, context),
				services: view.services,
				dashboardApi: view.getDashboardApi()
			};

			return Promise.resolve(options);
		},

		getEntries: function(context) {
			var glassContext = context.glassContext;

			return Promise.all([
				this._getShapeWidgets(glassContext),
				this._getImageLibrary(glassContext),
				this._getWidgets(glassContext)
			])
				.then(function(results) {
					var shapeWidgets = results[0];
					var images = results[1];
					var widgets = results[2];

					var entries = {
						widgets: widgets,
						images: {},
						shapeWidgets: shapeWidgets
					};

					if(images && images.list && images.list.length > 0) {
						entries.images = images;
					}
					return entries;
				})
				.then(this.getSpecs.bind(this, context));
		},

		getSpecs: function(context, entries) {
			const defaultColors = this._getWidgetColorsHelper(entries.widgets);

			// Notebook config check required here until Story 264685 complete
			const jupyterNotebookConfigPromise = context.glassContext
				.getCoreSvc('.Config')
				.getConfigValue('BIProxy.jupyter', false);

			const watsonStudioNotebookConfigPromise = context.glassContext
				.getCoreSvc('.Config')
				.getConfigValue('WatsonStudio.wsUrl', false);

			return Promise.all([
				this.createWidgetAddUIHelper(context),
				this._getShapeSpecHelper(entries.shapeWidgets, defaultColors),
				this._getImageLibrarySpecHelper(entries.images),
				jupyterNotebookConfigPromise,
				watsonStudioNotebookConfigPromise
			]).then(function(results) {
				const [helper, shapes, images, jupyterNotebook, watsonStudioNotebook] = results;
				const dashboardApi = this.getDashboardView(context).getDashboardApi();
				const icons = dashboardApi.getFeature('Icons');
				const spec = {};

				const options = {
					onItemClick: helper.addWidgetBySelection.bind(helper),
					onItemStartDrag: helper.addWidgetByDrag.bind(helper)
				};
				spec.options = options;
				spec.items = {};
				const modulePath = 'dashboard-core/js/dashboard/widgets/staticwidget/';

				// Basic widgets
				spec.items.widgets = [
					this._formulateWidgetSpec(stringResources.get('textWidgetLabel'), icons.getIcon('text-creation').id, modulePath, 'TextWidget'),
					this._formulateWidgetSpec(stringResources.get('imageWidgetLabel'), icons.getIcon('image').id, modulePath, 'ImageWidget'),
					this._formulateWidgetSpec(stringResources.get('mediaWidgetLabel'), icons.getIcon('video-file').id, modulePath, 'MediaWidget'),
					this._formulateWidgetSpec(stringResources.get('webpageWidgetLabel'), icons.getIcon('webpage').id, modulePath, 'WebpageWidget')
				];

				this._addWidgets(entries.widgets.list, 'basicWidgets', spec.items.widgets);
				spec.allItems = [].concat(spec.items.widgets);

				// Notebook
				if (jupyterNotebook || watsonStudioNotebook) {
					spec.items.advancedWidgets = [
						this._formulateWidgetSpec(stringResources.get('notebookWidgetLabel'), icons.getIcon('notebook').id, '', 'notebook/DashboardWidget')
					];
					spec.allItems = spec.allItems.concat(spec.items.advancedWidgets);
				}

				// Shapes
				spec.items.shapes = shapes;
				spec.allItems = spec.allItems.concat(shapes);

				// Images
				if(images && images.length > 0) {
					spec.items.images = images;
					spec.allItems = spec.allItems.concat(images);
				}
				return spec;
			}.bind(this));
		},

		_formulateWidgetSpec(widgetLabel, widgetIcon, modulePath, widgetModule) {
			return {
				label: widgetLabel,
				name: widgetLabel,
				icon: widgetIcon,
				content: {
					widget: modulePath + widgetModule,
					name: widgetLabel,
					title: widgetLabel
				}
			};
		},

		/**
		 * Adds widgets from the collection to the given section widget list if they have been attributed the given section name
		 */
		_addWidgets: function (widgetsList, sectionName, sectionWidgets) {
			const widgetsToAdd = (widgetsList || []).filter(widget => { return widget.sections && widget.sections.indexOf(sectionName) !== -1; });
			widgetsToAdd.forEach(widget => {
				sectionWidgets.push(this._formulateWidgetSpec(widget.label, widget.icon, '', widget.widget));
			});
		},

		_getShapeSpecHelper: function(shapes, colors) {
			const shapePromises = [];
			const shapeColors = colors && colors['com.ibm.bi.dashboard.widgets.shape'];

			_.each(shapes.list, function (shape) {

				shape.options.fillColor = shapeColors.fillColor;
				shape.options.borderColor = shapeColors.borderColor;
				shape.options.fillColorIndex = shapeColors.fillColorIndex;
				shape.options.borderColorIndex = shapeColors.borderColorInder;

				var promise = ShapeWidget.getDefaultSpec(shape.name, shape.options)
					.then(function(spec) {
						return {
							label: stringResources.get(shape.name),
							name: stringResources.get(shape.name),
							svg: spec.model.content,
							content: shape
						};
					});
				shapePromises.push(promise);
			});
			return Promise.all(shapePromises);
		},

		_getImageLibrarySpecHelper: function(images) {
			var imageSpecs = [];
			if (images.list) {
				_.each(images.list, function(image) {
					imageSpecs.push({
						label: image.name,
						name: image.name,
						image: image.options,
						content: image
					});
				});
			}
			return Promise.resolve(imageSpecs);
		},

		_getWidgetColorsHelper: function(widgets) {
			var widgetColors = {};
			if (widgets.list) {
				var getProperty = function (properties, color) {
					return _.find(properties, function(property) {
						return property.id === color;
					});
				};

				_.each(widgets.list, function(widget) {
					widgetColors[widget.id] = {};
					if (widget.propertyList) {
						var fillColor = getProperty(widget.propertyList, 'fillColor');
						var borderColor = getProperty(widget.propertyList, 'borderColor');
						widgetColors[widget.id].fillColor = fillColor && fillColor.defaultValue;
						widgetColors[widget.id].borderColor = borderColor && borderColor.defaultValue;
					}
				});
			}
			return widgetColors;
		},

		_getShapeWidgets: function(context) {
			return this._getEntries(context, 'com.ibm.bi.dashboard.shapes', 'dashboard-core/js/dashboard/widgets/staticwidget/ShapeWidget');
		},

		_getImageLibrary: function(context) {
			return this._getEntries(context, 'com.ibm.bi.common.media', 'dashboard-core/js/dashboard/widgets/staticwidget/ImageWidget');
		},

		_getWidgets: function(context) {
			return this._getEntries(context, 'com.ibm.bi.dashboard.widgets', 'dashboard-core/js/dashboard/widgets/staticwidget/StaticWidget');
		},

		_getEntries: function(context, perspectiveId, widgetModule) {
			return this.createWidgetAddUIHelper({ glassContext: context })
				.then(function(widgetAddUIHelper) {
					return widgetAddUIHelper.fetchWidgetListFromPerspective(perspectiveId);
				})
				.then(function(entries) {
					if(entries) {
						_.each(entries.list, function(entry) {
							if(!entry.widget) {
								entry.widget = widgetModule;
							}
						});
					}
					return entries;
				});
		}
	});

	return ActionHandler;
});
